import { captureError } from '@smg-real-estate/browser-rum';
import { RankType, type Listing } from '@smg-real-estate/hg-rets-schema';
import type { ActionContext, Commit } from 'vuex';

import type { TopListingAbos, ToplistingPrices } from '@/api/toplistingService';
import * as ToplistingApiService from '@/api/toplistingService';
import { ContractModel } from '@/api/toplistingService';
import { ApiCallStatus, findInsertionById } from '@/store/insertions/InsertionsStore';
import type { InsertionFunnelState } from '@/store/types/insertion-funnel-store';
import {
  ToplistingModalAction,
  ToplistingVariant,
  type BookToplistingRequest,
  type Insertion,
  type ToplistingData,
} from '@/types';

const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 10000];
const NO_TOPLISTING_SIGNATURE = 'none';

const getToplistingSignature = (data: ToplistingData): string =>
  [data.listingType, data.contractModel, data.beginRequest, data.endRequest ?? ''].join('|');

const getExpectedSignatureFromBooking = (booking: BookToplistingRequest): string =>
  [booking.rankType, booking.contractModel, booking.beginRequest, booking.endRequest ?? ''].join(
    '|',
  );

const getExpectedSignatureFromFunnelListing = (listing?: Listing): string | null => {
  const valueAddedServices = listing?.valueAddedServices;
  if (!valueAddedServices) {
    return null;
  }

  if (valueAddedServices.isLifetimeToplisting) {
    return [
      RankType.Top,
      ContractModel.LIFETIME,
      valueAddedServices.rank?.start ?? '',
      valueAddedServices.rank?.end ?? '',
    ].join('|');
  }

  const rankType = valueAddedServices.rank?.type;
  if (!rankType) {
    return null;
  }

  const listingRankType = rankType === RankType.Premium ? RankType.Premium : RankType.Top;

  return [
    listingRankType,
    ContractModel.DAILY,
    valueAddedServices.rank?.start ?? '',
    valueAddedServices.rank?.end ?? '',
  ].join('|');
};

const hasActiveToplisting = (data?: ToplistingData): boolean =>
  data?.listingType === RankType.Top || data?.listingType === RankType.Premium;

const matchesExpectedSignature = (
  data: ToplistingData | undefined,
  expectedSignature: string,
): boolean => {
  if (expectedSignature === NO_TOPLISTING_SIGNATURE) {
    return !hasActiveToplisting(data);
  }

  if (!data) {
    return false;
  }

  const actualSignature = getToplistingSignature(data);
  return (
    actualSignature === expectedSignature || actualSignature.startsWith(`${expectedSignature}|`)
  );
};

const resolveUserIdForInsertion = (
  context: ActionContext<ToplistingState, InsertionFunnelState>,
  insertionId: string,
): string | undefined => {
  const insertionFromList = findInsertionById(
    context.rootState.insertions?.insertions ?? [],
    insertionId,
  );

  return (
    insertionFromList?.userId ??
    context.state.toplistingInsertion?.userId ??
    context.rootGetters.getActiveAgencyPersonId ??
    context.rootGetters.getUser?.logonId
  );
};

const refetchToplistingIfMatches = async (
  context: ActionContext<ToplistingState, InsertionFunnelState>,
  userId: string,
  insertionId: string,
  expectedSignature: string,
): Promise<boolean> => {
  try {
    const updatedInsertion = await context.dispatch('refetchInsertionById', {
      userId,
      insertionId,
    });

    return matchesExpectedSignature(updatedInsertion.toplistingData, expectedSignature);
  } catch (error) {
    captureError(error);
    return false;
  }
};

const finishToplistingProcessing = (
  context: ActionContext<ToplistingState, InsertionFunnelState>,
  insertionId: string,
  expectedSignature: string,
): void => {
  if (expectedSignature === NO_TOPLISTING_SIGNATURE) {
    context.commit('removeFromInsertionIdsWithRecentlyDeletedToplisting', insertionId);
    return;
  }

  context.commit('removeFromInsertionIdsWithRecentlyUpdatedToplisting', insertionId);
};

const startToplistingPolling = (
  context: ActionContext<ToplistingState, InsertionFunnelState>,
  insertionId: string,
): void => {
  context.dispatch('pollToplistingUpdateStatus', insertionId).catch((error) => {
    captureError(error);
  });
};

export interface ToplistingState {
  toplistingInsertionId?: string;
  toplistingModalAction: ToplistingModalAction;
  toplistingApiCallStatus: ApiCallStatus;
  insertionIdsWithRecentlyUpdatedToplisting: Array<string>;
  insertionIdsWithRecentlyDeletedToplisting: Array<string>;
  expectedToplistingSignatures: Record<string, string>;
  toplistingInsertion?: Insertion;
  topListingAbos?: TopListingAbos;
  topListingPrices?: ToplistingPrices;
}

const getInitialToplistingState = (): ToplistingState => ({
  toplistingInsertionId: undefined,
  toplistingModalAction: ToplistingModalAction.CREATE,
  toplistingApiCallStatus: ApiCallStatus.READY,
  insertionIdsWithRecentlyUpdatedToplisting: [],
  insertionIdsWithRecentlyDeletedToplisting: [],
  expectedToplistingSignatures: {},
  toplistingInsertion: undefined,
  topListingAbos: undefined,
  topListingPrices: undefined,
});

const getters = {
  getToplistingPrices: (state: ToplistingState): ToplistingPrices | undefined =>
    state.topListingPrices,
  getTopListingAbos: (state: ToplistingState): TopListingAbos | undefined => state.topListingAbos,
  getToplistingInsertionId: (state: ToplistingState): string | undefined =>
    state.toplistingInsertionId,
  getToplistingModalAction: (state: ToplistingState): string | undefined =>
    state.toplistingModalAction,
  getToplistingDataByInsertionId:
    (state: ToplistingState, _getters: object, rootState: InsertionFunnelState) =>
    (id: string): ToplistingData | undefined =>
      state.toplistingInsertion !== undefined
        ? state.toplistingInsertion.toplistingData
        : findInsertionById(rootState.insertions.insertions, id).toplistingData,
  getObjectReferenceFromInsertionId:
    (state: ToplistingState, _getters: object, rootState: InsertionFunnelState) =>
    (id: string): string =>
      findInsertionById(rootState.insertions?.insertions || [], id)?.objectReference ??
      state.toplistingInsertion!.objectReference,
  isToplistingApiCallInProgress: (state: ToplistingState): boolean =>
    state.toplistingApiCallStatus === ApiCallStatus.IN_PROGRESS,
  getInsertionIdsWithRecentlyUpdatedToplisting: (state: ToplistingState): string[] =>
    state.insertionIdsWithRecentlyUpdatedToplisting,
  getInsertionIdsWithRecentlyDeletedToplisting: (state: ToplistingState): string[] =>
    state.insertionIdsWithRecentlyDeletedToplisting,
};

const clearExpectedToplistingSignature = (state: ToplistingState, insertionId: string): void => {
  const { [insertionId]: _removed, ...remainingSignatures } = state.expectedToplistingSignatures;
  state.expectedToplistingSignatures = remainingSignatures;
};

const mutations = {
  setTopListingAbos(state: ToplistingState, topListingAbos: TopListingAbos): void {
    state.topListingAbos = topListingAbos;
  },
  setToplistingInsertionId(state: ToplistingState, insertionId: string): void {
    state.toplistingInsertionId = insertionId;
  },
  setToplistingInsertion(state: ToplistingState, insertion: Insertion): void {
    state.toplistingInsertion = insertion;
  },
  clearToplistingInsertionId(state: ToplistingState): void {
    state.toplistingInsertionId = undefined;
  },
  setToplistingModalAction(state: ToplistingState, toplistingAction: ToplistingModalAction): void {
    state.toplistingModalAction = toplistingAction;
  },
  setToplistingApiCallStatus(state: ToplistingState, apiCallStatus: ApiCallStatus): void {
    state.toplistingApiCallStatus = apiCallStatus;
  },
  setTopListingPrices(state: ToplistingState, topListingPrices: ToplistingPrices): void {
    state.topListingPrices = topListingPrices;
  },
  appendToInsertionIdsWithRecentlyUpdatedToplisting(
    state: ToplistingState,
    payload: { insertionId: string; expectedSignature: string },
  ): void {
    state.insertionIdsWithRecentlyUpdatedToplisting = [
      ...state.insertionIdsWithRecentlyUpdatedToplisting,
      payload.insertionId,
    ];
    state.expectedToplistingSignatures = {
      ...state.expectedToplistingSignatures,
      [payload.insertionId]: payload.expectedSignature,
    };
  },
  removeFromInsertionIdsWithRecentlyUpdatedToplisting(
    state: ToplistingState,
    insertionIdWithRecentlyUpdatedToplisting: string,
  ): void {
    state.insertionIdsWithRecentlyUpdatedToplisting =
      state.insertionIdsWithRecentlyUpdatedToplisting.filter(
        (insertionId) => insertionId !== insertionIdWithRecentlyUpdatedToplisting,
      );
    clearExpectedToplistingSignature(state, insertionIdWithRecentlyUpdatedToplisting);
  },
  appendToInsertionIdsWithRecentlyDeletedToplisting(
    state: ToplistingState,
    insertionIdWithRecentlyDeletedToplisting: string,
  ): void {
    state.insertionIdsWithRecentlyDeletedToplisting = [
      ...state.insertionIdsWithRecentlyDeletedToplisting,
      insertionIdWithRecentlyDeletedToplisting,
    ];
    state.expectedToplistingSignatures = {
      ...state.expectedToplistingSignatures,
      [insertionIdWithRecentlyDeletedToplisting]: NO_TOPLISTING_SIGNATURE,
    };
  },
  removeFromInsertionIdsWithRecentlyDeletedToplisting(
    state: ToplistingState,
    insertionIdWithRecentlyDeletedToplisting: string,
  ): void {
    state.insertionIdsWithRecentlyDeletedToplisting =
      state.insertionIdsWithRecentlyDeletedToplisting.filter(
        (insertionId) => insertionId !== insertionIdWithRecentlyDeletedToplisting,
      );
    clearExpectedToplistingSignature(state, insertionIdWithRecentlyDeletedToplisting);
  },
};

const actions = {
  async fetchTopListingAbos(
    context: ActionContext<ToplistingState, InsertionFunnelState>,
  ): Promise<void> {
    // TODO: how to get the agencyId, before agencySwitcher is used?
    const activeAgencyId = context.getters.getActiveAgencyId;

    if (!activeAgencyId) return;

    try {
      const topListingAbos = await ToplistingApiService.fetchTopListingAbos(activeAgencyId);
      context.commit('setTopListingAbos', topListingAbos);
    } catch (error) {
      captureError(error);
    }
  },
  async pollToplistingUpdateStatus(
    context: ActionContext<ToplistingState, InsertionFunnelState>,
    insertionId: string,
  ): Promise<void> {
    const expectedSignature = context.state.expectedToplistingSignatures[insertionId];
    if (!expectedSignature) {
      return;
    }

    const userId = resolveUserIdForInsertion(context, insertionId);
    if (!userId) {
      finishToplistingProcessing(context, insertionId, expectedSignature);
      return;
    }

    for (const delayMs of POLL_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      if (await refetchToplistingIfMatches(context, userId, insertionId, expectedSignature)) {
        finishToplistingProcessing(context, insertionId, expectedSignature);
        return;
      }
    }

    finishToplistingProcessing(context, insertionId, expectedSignature);
  },
  async bookToplisting(
    context: ActionContext<ToplistingState, InsertionFunnelState>,
    data: {
      insertionId: string;
      toplisting: BookToplistingRequest;
      action: ToplistingModalAction;
    },
  ): Promise<void> {
    const objectReference = context.getters.getObjectReferenceFromInsertionId(data.insertionId);
    await handleToplistingAsyncAction(async () => {
      await ToplistingApiService.bookToplisting(objectReference, data.toplisting);

      context.commit('appendToInsertionIdsWithRecentlyUpdatedToplisting', {
        insertionId: data.insertionId,
        expectedSignature: getExpectedSignatureFromBooking(data.toplisting),
      });

      context.commit(
        'setListingActionSuccess',
        `manageAdsPage.toasts.toplistingActionSuccess.${data.toplisting.rankType}.${data.action}`,
      );

      startToplistingPolling(context, data.insertionId);
    }, context.commit);
  },
  async bookToplistingAbo(
    context: ActionContext<ToplistingState, InsertionFunnelState>,
    data: {
      insertionId: string;
      action: ToplistingModalAction;
    },
  ): Promise<void> {
    // TODO: how to get the agencyId, before agencySwitcher is used?
    const activeAgencyId = context.getters.getActiveAgencyId || context.getters.getUser.logonId;

    const objectReference = context.getters.getObjectReferenceFromInsertionId(data.insertionId);
    await handleToplistingAsyncAction(async () => {
      await ToplistingApiService.bookToplistingAbo(objectReference, activeAgencyId);

      context.commit('appendToInsertionIdsWithRecentlyUpdatedToplisting', {
        insertionId: data.insertionId,
        expectedSignature: `${RankType.Top}|${ContractModel.ABO}`,
      });

      context.commit(
        'setListingActionSuccess',
        `manageAdsPage.toasts.toplistingActionSuccess.${ToplistingVariant.TopAbo}.CREATE`,
      );

      // Increase the TL Abo ActiveSlots by 1 to display the correct count until the next fetchTopListingAbos call
      context.commit('setTopListingAbos', {
        ...context.getters.getTopListingAbos,
        activeSlots: (context.getters.getTopListingAbos.activeSlots += 1),
      });

      startToplistingPolling(context, data.insertionId);
    }, context.commit);
  },
  async deleteToplisting(
    context: ActionContext<ToplistingState, InsertionFunnelState>,
    insertionId: string,
  ): Promise<void> {
    const objectReference = context.getters.getObjectReferenceFromInsertionId(insertionId);
    await handleToplistingAsyncAction(async () => {
      await ToplistingApiService.deleteToplisting(objectReference);

      context.commit('appendToInsertionIdsWithRecentlyDeletedToplisting', insertionId);

      context.commit('setListingActionSuccess', 'manageAdsPage.toasts.toplistingWillBeDeactivated');

      startToplistingPolling(context, insertionId);
    }, context.commit);
  },
  trackToplistingFromFunnelListing(
    context: ActionContext<ToplistingState, InsertionFunnelState>,
    payload: { insertionId: string; listing?: Listing },
  ): void {
    const expectedSignature = getExpectedSignatureFromFunnelListing(payload.listing);
    if (!expectedSignature) {
      return;
    }

    const insertion = findInsertionById(
      context.rootState.insertions?.insertions ?? [],
      payload.insertionId,
    );
    if (matchesExpectedSignature(insertion?.toplistingData, expectedSignature)) {
      return;
    }

    if (
      !(context.state.insertionIdsWithRecentlyUpdatedToplisting ?? []).includes(payload.insertionId)
    ) {
      context.commit('appendToInsertionIdsWithRecentlyUpdatedToplisting', {
        insertionId: payload.insertionId,
        expectedSignature,
      });
    }

    startToplistingPolling(context, payload.insertionId);
  },
};

export const handleToplistingAsyncAction = async (
  action: () => Promise<any>,
  commit: Commit,
): Promise<void> => {
  try {
    commit('setToplistingApiCallStatus', ApiCallStatus.IN_PROGRESS); // start spinner
    commit('setApiCallStatus', ApiCallStatus.IN_PROGRESS);
    await action();
    commit('setToplistingApiCallStatus', ApiCallStatus.SUCCESS); // stop spinner
    commit('setApiCallStatus', ApiCallStatus.SUCCESS);
  } catch (error) {
    commit('setToplistingApiCallStatus', ApiCallStatus.READY); // reset toplistingApiCallStatus
    commit('setApiCallStatus', ApiCallStatus.ERROR); // pop error to higher level so it calls HgToast
    captureError(error);
  }
};

const _default = {
  state: getInitialToplistingState,
  getters,
  mutations,
  actions,
};
export default _default;
